import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { createPreviewSession, PreviewSession, revokePreviewSession, startPreviewWebRTC } from '../services/streamsService';
import { adjustRuntimeTelemetryDebugGauge, recordRuntimeTelemetryDebug } from '../utils/runtimeTelemetryDebug';

const REFOCUS_PREVIEW_DELAY_MS = 1500;
export type PreviewType = 'webrtc' | 'hls';
type PreviewConnectionState = 'idle' | 'initializing' | 'connecting' | 'connected' | 'retrying' | 'unavailable';

const previewStateLabel: Partial<Record<PreviewConnectionState, string>> = {
  initializing: 'Initializing Preview',
  connecting: 'Connecting Preview',
  retrying: 'Retrying Preview',
};

const isBenignAbortError = (error: any) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    error?.name === 'AbortError' ||
    error?.code === 'ERR_CANCELED' ||
    message.includes('aborted by the user agent') ||
    message.includes('the play() request was interrupted') ||
    message.includes('the fetching process for the media resource was aborted')
  );
};

const isTerminalPreviewError = (error: any) => {
  const status = Number(error?.response?.status || error?.status || 0);
  return status === 400;
};

const getRetryDelayMs = (attempt: number) => {
  const exponential = Math.min(10000, 1000 * (2 ** Math.min(attempt, 4)));
  return Math.round(exponential * (0.8 + Math.random() * 0.4));
};

const getPreviewErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

const waitForIceGathering = (peer: RTCPeerConnection) => new Promise<void>((resolve) => {
  if (peer.iceGatheringState === 'complete') {
    resolve();
    return;
  }

  const handleChange = () => {
    if (peer.iceGatheringState === 'complete') {
      peer.removeEventListener('icegatheringstatechange', handleChange);
      resolve();
    }
  };

  peer.addEventListener('icegatheringstatechange', handleChange);
});

interface VideoPlayerProps {
  channelId?: number | null;
  channelHashId?: string | null;
  streamName: string;
  streamKeyPin: string;
  isLive: boolean;
  autoPlay?: boolean;
  previewType?: PreviewType;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  channelId = null,
  channelHashId = null,
  streamName,
  streamKeyPin,
  isLive,
  autoPlay = true,
  previewType = 'webrtc',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const previewSessionRef = useRef<PreviewSession | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const startTimerRef = useRef<number | null>(null);
  const retryAttemptRef = useRef(0);
  const sessionRequestRef = useRef<AbortController | null>(null);
  const wasHiddenRef = useRef(false);
  const delayPreviewOnNextStartRef = useRef(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewDeferred, setPreviewDeferred] = useState(false);
  const [previewState, setPreviewState] = useState<PreviewConnectionState>('idle');
  const [resumeRequest, setResumeRequest] = useState(0);
  const [isVisible, setIsVisible] = useState(() => document.visibilityState !== 'hidden');

  useEffect(() => {
    const onVisibilityChange = () => {
      const nextVisible = document.visibilityState !== 'hidden';
      if (!nextVisible) {
        wasHiddenRef.current = true;
        setIsVisible(false);
        return;
      }
      if (wasHiddenRef.current) {
        delayPreviewOnNextStartRef.current = true;
        wasHiddenRef.current = false;
      }
      setIsVisible(true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    const clearRetry = () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const clearStart = () => {
      if (startTimerRef.current) {
        window.clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
    };

    const cleanupPeer = () => {
      recordRuntimeTelemetryDebug('preview:cleanup-start');
      clearRetry();
      clearStart();
      sessionRequestRef.current?.abort();
      sessionRequestRef.current = null;
      const previewSession = previewSessionRef.current;
      previewSessionRef.current = null;
      if (previewSession) {
        revokePreviewSession(
          { id: channelId, channelHashId: channelHashId || undefined },
          previewSession.id,
          channelHashId
        ).catch(() => {});
      }
      const hls = hlsRef.current;
      hlsRef.current = null;
      if (hls) {
        hls.destroy();
      }
      const peer = peerRef.current;
      peerRef.current = null;
      if (peer) {
        peer.ontrack = null;
        peer.onconnectionstatechange = null;
        peer.close();
        adjustRuntimeTelemetryDebugGauge('preview:active-peer-connections', -1);
      }
      const video = videoRef.current;
      if (video) {
        try {
          video.pause();
        } catch {}
        video.onerror = null;
        video.srcObject = null;
        video.removeAttribute('src');
        try {
          video.load();
        } catch (error) {
          if (!isBenignAbortError(error)) {
            recordRuntimeTelemetryDebug('preview:cleanup-load-error', { message: String((error as any)?.message || error) });
          }
        }
      }
      recordRuntimeTelemetryDebug('preview:cleanup-complete');
    };

    const cleanupPreview = () => cleanupPeer();

    if (!isLive || !isVisible) {
      cleanupPreview();
      setPreviewState('idle');
      setPreviewDeferred(false);
      setPreviewError(null);
      return cleanupPreview;
    }

    if (!channelId) {
      cleanupPreview();
      setPreviewState('unavailable');
      setPreviewDeferred(false);
      setPreviewError('Preview path is unavailable for this channel.');
      return cleanupPreview;
    }

    let cancelled = false;

    const scheduleRetry = (reason = 'transport') => {
      if (cancelled) return;
      if (retryTimerRef.current !== null) return;
      const attempt = retryAttemptRef.current;
      retryAttemptRef.current += 1;
      const delayMs = getRetryDelayMs(attempt);
      setPreviewState('retrying');
      recordRuntimeTelemetryDebug('preview:retry-scheduled', { mode: previewType, reason, attempt: attempt + 1, delayMs });
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        initializePreview().catch(() => {});
      }, delayMs);
    };

    const initializeWebRtcPreview = async (previewSession: PreviewSession) => {
      recordRuntimeTelemetryDebug('preview:init-start');
      setPreviewError(null);
      setPreviewState('initializing');

      const video = videoRef.current;
      if (!video) return;

      const peer = new RTCPeerConnection({ bundlePolicy: 'max-bundle' });
      peerRef.current = peer;
      adjustRuntimeTelemetryDebugGauge('preview:active-peer-connections', 1);

      const mediaStream = new MediaStream();
      video.srcObject = mediaStream;
      setPreviewState('connecting');

      peer.addTransceiver('video', { direction: 'recvonly' });
      peer.addTransceiver('audio', { direction: 'recvonly' });

      peer.ontrack = (event) => {
        event.streams[0]?.getTracks().forEach((track) => {
          mediaStream.addTrack(track);
        });
      };

      peer.onconnectionstatechange = () => {
        if (cancelled) return;

        if (peer.connectionState === 'connected') {
          retryAttemptRef.current = 0;
          setPreviewError(null);
          setPreviewState('connected');
          return;
        }

        if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected') {
          setPreviewError('Preview connection was interrupted. Retrying.');
          scheduleRetry('webrtc-connection');
        }
      };

      try {
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        await waitForIceGathering(peer);
        if (cancelled || peerRef.current !== peer) return;

        const localDescription = peer.localDescription?.sdp || offer.sdp || '';
        if (!previewSession.webrtcUrl) throw new Error('WebRTC preview URL is unavailable.');
        const answerSdp = await startPreviewWebRTC(previewSession.webrtcUrl, localDescription);
        if (cancelled || peerRef.current !== peer) return;

        await peer.setRemoteDescription({
          type: 'answer',
          sdp: answerSdp,
        });
        recordRuntimeTelemetryDebug('preview:init-complete');

        if (autoPlay) {
          video.muted = true;
          video.play().catch((error) => {
            if (!cancelled && !isBenignAbortError(error)) {
              setPreviewError('Preview unavailable.');
            }
          });
        }
      } catch (error: any) {
        if (cancelled) return;
        if (isBenignAbortError(error)) return;
        if (isTerminalPreviewError(error)) {
          cleanupPreview();
          setPreviewState('unavailable');
          setPreviewError(getPreviewErrorMessage(error, 'Preview route is unavailable for this channel.'));
          return;
        }
        setPreviewError(getPreviewErrorMessage(error, 'Preview unavailable.'));
        scheduleRetry('webrtc-negotiate');
      }
    };

    const initializeHlsPreview = async (previewSession: PreviewSession) => {
      recordRuntimeTelemetryDebug('preview:hls-init-start');
      setPreviewError(null);
      setPreviewState('initializing');

      const video = videoRef.current;
      if (!video) return;

      try {
        if (cancelled) return;
        setPreviewState('connecting');
        const urls = previewSession.hlsUrl ? [previewSession.hlsUrl] : [];
        if (urls.length === 0) {
          setPreviewError('HLS preview URL is unavailable.');
          scheduleRetry();
          return;
        }

        let urlIndex = 0;
        const attachHlsUrl = (url: string) => {
          const previous = hlsRef.current;
          hlsRef.current = null;
          if (previous) previous.destroy();

          if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = url;
            return;
          }
          if (!Hls.isSupported()) {
            setPreviewError('HLS preview is not supported in this browser.');
            setPreviewState('unavailable');
            return;
          }
          const hls = new Hls({
            lowLatencyMode: true,
            backBufferLength: 30,
            maxBufferLength: 12,
            xhrSetup: (xhr) => { xhr.withCredentials = true; },
          });
          hlsRef.current = hls;
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (cancelled) return;
            const responseCode = Number((data as any)?.response?.code || (data as any)?.networkDetails?.status || 0);
            if (responseCode === 401 || responseCode === 403) {
              hls.stopLoad();
              recordRuntimeTelemetryDebug('preview:hls-auth-refresh', { responseCode });
              setPreviewError('HLS preview authorization is being refreshed.');
              scheduleRetry('hls-auth');
              return;
            }
            if (!data.fatal) return;
            const nextUrl = urls[urlIndex + 1];
            if (nextUrl) {
              urlIndex += 1;
              attachHlsUrl(nextUrl);
              return;
            }
            setPreviewError('HLS preview was interrupted. Retrying.');
            scheduleRetry(responseCode ? `hls-${responseCode}` : 'hls-network');
          });
          hls.loadSource(url);
          hls.attachMedia(video);
        };

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          const handleNativeError = () => {
            if (cancelled) return;
            const nextUrl = urls[urlIndex + 1];
            if (nextUrl) {
              urlIndex += 1;
              video.src = nextUrl;
              return;
            }
            setPreviewError('HLS preview was interrupted. Retrying.');
            scheduleRetry('hls-native');
          };
          video.onerror = handleNativeError;
          const url = urls[urlIndex];
          video.src = url;
        } else {
          attachHlsUrl(urls[urlIndex]);
        }

        if (autoPlay) {
          video.muted = true;
          video.play().catch((error) => {
            if (!cancelled && !isBenignAbortError(error)) {
              setPreviewError('HLS preview unavailable.');
            }
          });
        }
        recordRuntimeTelemetryDebug('preview:hls-init-complete');
      } catch (error: any) {
        if (cancelled) return;
        if (isBenignAbortError(error)) return;
        if (isTerminalPreviewError(error)) {
          cleanupPreview();
          setPreviewState('unavailable');
          setPreviewError(getPreviewErrorMessage(error, 'HLS preview route is unavailable for this channel.'));
          return;
        }
        setPreviewError(getPreviewErrorMessage(error, 'HLS preview unavailable.'));
        scheduleRetry('hls-initialize');
      }
    };

    const initializePreview = async () => {
      cleanupPreview();
      setPreviewError(null);
      setPreviewState('initializing');
      try {
        const requestController = new AbortController();
        sessionRequestRef.current = requestController;
        const previewSession = await createPreviewSession(
          { id: channelId, channelHashId: channelHashId || undefined },
          previewType,
          channelHashId,
          requestController.signal,
        );
        if (sessionRequestRef.current === requestController) sessionRequestRef.current = null;
        if (cancelled) {
          revokePreviewSession(
            { id: channelId, channelHashId: channelHashId || undefined },
            previewSession.id,
            channelHashId
          ).catch(() => {});
          return;
        }
        previewSessionRef.current = previewSession;
        if (previewSession.mode === 'hls') return initializeHlsPreview(previewSession);
        return initializeWebRtcPreview(previewSession);
      } catch (error: any) {
        if (cancelled || isBenignAbortError(error)) return;
        if (isTerminalPreviewError(error)) {
          cleanupPreview();
          setPreviewState('unavailable');
          setPreviewError(getPreviewErrorMessage(error, 'Preview route is unavailable for this channel.'));
          return;
        }
        setPreviewError(getPreviewErrorMessage(error, 'Preview unavailable.'));
        scheduleRetry('session');
      }
    };

    const delayStart = delayPreviewOnNextStartRef.current;
    delayPreviewOnNextStartRef.current = false;
    if (delayStart) {
      setPreviewDeferred(true);
      recordRuntimeTelemetryDebug('preview:refocus-deferred', { delayMs: REFOCUS_PREVIEW_DELAY_MS });
      startTimerRef.current = window.setTimeout(() => {
        startTimerRef.current = null;
        setPreviewDeferred(false);
        initializePreview().catch(() => {});
      }, REFOCUS_PREVIEW_DELAY_MS);
    } else {
      setPreviewDeferred(false);
      initializePreview().catch(() => {});
    }

    return () => {
      cancelled = true;
      cleanupPreview();
    };
  }, [autoPlay, channelHashId, channelId, isLive, isVisible, previewType, resumeRequest]);

  if (!isLive) {
    return (
      <Box sx={{ width: '100%', height: '100%', minHeight: 'inherit', position: 'relative', backgroundColor: 'common.black', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <Typography sx={{ color: 'common.white' }}>Stream is Offline</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 'inherit', position: 'relative', backgroundColor: 'common.black', overflow: 'hidden' }}>
      <video
        ref={videoRef}
        controls
        muted
        playsInline
        autoPlay={autoPlay}
        onPlaying={() => { retryAttemptRef.current = 0; setPreviewState('connected'); }}
        style={{ width: '100%', height: '100%', minHeight: 'inherit', objectFit: 'contain', display: 'block' }}
      />
      <Tooltip title="Live Preview">
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 8, sm: 12 },
            left: { xs: 8, sm: 12 },
            width: { xs: 20, sm: 22 },
            height: { xs: 20, sm: 22 },
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'error.main',
            color: 'var(--on-danger)',
            zIndex: 3,
            pointerEvents: 'none',
            boxShadow: '0 0 0 2px rgba(var(--bg-body-rgb), 0.28)',
          }}
        >
          <FiberManualRecordIcon sx={{ fontSize: '8px !important' }} />
        </Box>
      </Tooltip>
      {previewDeferred && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.42)',
            zIndex: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              delayPreviewOnNextStartRef.current = false;
              setResumeRequest((prev) => prev + 1);
            }}
          >
            Resume Preview
          </Button>
        </Box>
      )}
      {!previewDeferred && previewStateLabel[previewState] && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.42)',
            color: 'common.white',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {previewStateLabel[previewState]}
          </Typography>
        </Box>
      )}
      {previewError && (
        <Alert
          severity="warning"
          sx={{
            position: 'absolute',
            left: { xs: 8, sm: 12 },
            right: { xs: 8, sm: 12 },
            bottom: { xs: 8, sm: 12 },
            borderRadius: 0,
            py: 0.5,
            zIndex: 4,
            maxHeight: { xs: 64, sm: 'none' },
            overflow: 'hidden',
          }}
        >
          {previewError}
        </Alert>
      )}
    </Box>
  );
};
